import './MailComposer.scss';
import { ReactNode } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { MdDelete } from 'react-icons/md';
import { Button } from '../Button';
import { Appearance, Design, DesignType, Intent, Size, SizeType } from '../designConstants';
import { IconButton } from '../IconButton';
import { ListSkeleton, RectSkeleton, Skeleton } from '../Skeleton';

const SKELETON_LINE_HEIGHT = 30;
const SKELETON_PADDING = 10;
const SKELETON_BORDER_RADIUS = 5;

const MailerSkeleton = () => (
  <Skeleton>
    <ListSkeleton
      alignTop
      lineHeight={SKELETON_LINE_HEIGHT}
      lineSpacing={SKELETON_PADDING / 2}
      horizontalPadding={SKELETON_PADDING}
      verticalPadding={SKELETON_PADDING}
      height={height => height / 2}
      borderRadius={SKELETON_BORDER_RADIUS}
    />
    <RectSkeleton
      // We want padding on x/y/width, so we supply it as a prop. But it will also deduct from
      // height, so add it back in.
      height={SKELETON_LINE_HEIGHT + 2 * SKELETON_PADDING}
      y={height => height / 2}
      padding={SKELETON_PADDING}
    />
    <RectSkeleton
      height={height => height / 2 - SKELETON_LINE_HEIGHT - SKELETON_PADDING}
      y={height => height / 2 + SKELETON_LINE_HEIGHT + SKELETON_PADDING}
      padding={SKELETON_PADDING}
    />
  </Skeleton>
);

interface MailComposerProps {
  // False renders the skeleton in place of the form -- e.g. while the pre-filled mail info is
  // still loading.
  loaded: boolean;
  to: string;
  onToChange: (value: string) => void;
  cc: string[];
  onCCChange: (updater: (current: string[]) => string[]) => void;
  // Extra CC rows shown read-only below the editable ones (e.g. a fixed distro address, or the
  // sending user's own email). Defaults to none.
  readOnlyCC?: string[];
  subject: string;
  onSubjectChange: (value: string) => void;
  bodyText: string;
  onBodyTextChange: (value: string) => void;
  onCancel: () => void;
  onSend: () => void;
  sendDisabled?: boolean;
  sendLoading?: boolean;
  // Object URL (or any URL) for the <iframe> preview -- undefined renders the skeleton instead.
  previewUrl?: string;
  // Both callers' Cancel/Send buttons predate this extraction with slightly different styling;
  // parameterized here (rather than picked for both) so neither page's existing look changes.
  buttonSize?: SizeType;
  cancelButtonDesign?: DesignType;
  addCcButtonClassName?: string;
}

// Shared by CreditMemoModal (Credit Memos) and SendOrderView (Streaming Buying) -- both send a
// generated PDF as an email attachment via a To/CC/Subject/Body form next to a live PDF preview
// pane, using the same markdown-ish body-text convention (*bold*, _italics_). Owns the form,
// skeleton, and PDF preview pane; callers own their own domain logic (seeding the fields, the
// actual send request, and any follow-up status update).
export const MailComposer = ({
  loaded,
  to,
  onToChange,
  cc,
  onCCChange,
  readOnlyCC = [],
  subject,
  onSubjectChange,
  bodyText,
  onBodyTextChange,
  onCancel,
  onSend,
  sendDisabled = false,
  sendLoading = false,
  previewUrl,
  buttonSize = Size.LG,
  cancelButtonDesign = Design.BRAND,
  addCcButtonClassName
}: MailComposerProps): ReactNode => (
  <div className='send-view'>
    <div className='mail-pane'>
      {loaded ? (
        <>
          <div className='emails'>
            <InputGroup size='sm'>
              <InputGroup.Text className='input-label'>To</InputGroup.Text>
              <Form.Control
                data-testid='to-input'
                value={to}
                onChange={e => onToChange(e.target.value)}
              />
            </InputGroup>
            <div className='ccs'>
              {cc.map((email, i) => (
                <InputGroup key={i} size='sm'>
                  <InputGroup.Text className='input-label'>CC</InputGroup.Text>
                  <Form.Control
                    data-testid={`cc-input-${i}`}
                    value={email}
                    onChange={e =>
                      onCCChange(current =>
                        current.map((c, idx) => (idx === i ? e.target.value : c))
                      )
                    }
                  />
                  <IconButton
                    design={Intent.ERROR}
                    appearance={Appearance.OUTLINED}
                    size={Size.SM}
                    onClick={() => onCCChange(current => current.filter((_c, idx) => idx !== i))}
                    data-testid={`remove-cc-${i}`}
                  >
                    <MdDelete />
                  </IconButton>
                </InputGroup>
              ))}
              {readOnlyCC.map((email, i) => (
                <InputGroup key={`readonly_${i}`} size='sm'>
                  <InputGroup.Text className='input-label'>CC</InputGroup.Text>
                  <Form.Control readOnly value={email} data-testid={`readonly-cc-${i}`} />
                </InputGroup>
              ))}
            </div>
            <Button
              data-testid='add-cc-button'
              className={addCcButtonClassName}
              size={buttonSize}
              onClick={() => onCCChange(current => [...current, ''])}
            >
              Add CC
            </Button>
          </div>
          <div className='content'>
            <InputGroup size='sm'>
              <InputGroup.Text>Subject</InputGroup.Text>
              <Form.Control
                data-testid='subject-input'
                value={subject}
                onChange={e => onSubjectChange(e.target.value)}
              />
            </InputGroup>
            <Form.Control
              as='textarea'
              className='body-text-area'
              data-testid='body-textarea'
              value={bodyText}
              onChange={e => onBodyTextChange(e.target.value)}
            />
            <div className='hint'>
              You can use *<strong>Bold</strong>* and _<em>Italics</em>_, which will appear in the
              email.
            </div>
          </div>
          <div className='button-row'>
            <Button
              data-testid='cancel-send-button'
              size={buttonSize}
              design={cancelButtonDesign}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              data-testid='send-button'
              size={buttonSize}
              disabled={sendDisabled}
              loading={sendLoading}
              onClick={onSend}
            >
              Send
            </Button>
          </div>
        </>
      ) : (
        <MailerSkeleton />
      )}
    </div>
    <div className='pdf-viewer'>
      {previewUrl ? (
        <iframe src={previewUrl} title='pdfViewer' data-testid='pdf-preview' />
      ) : (
        <Skeleton>
          <RectSkeleton padding={SKELETON_PADDING} />
        </Skeleton>
      )}
    </div>
  </div>
);
